import { FiUser } from 'react-icons/fi';
import Button from '~/components/button';

import * as St from '../Client/styles';
import { DebtGroup } from './debt';
import { formatDate, formatMoney } from './format';

export interface ClientField {
  label: string;
  value: string;
}

interface ClientHeaderProps {
  photo?: string;
  name?: string;
  fields: ClientField[];
}

interface ClientDebtColumnProps {
  dueDate?: string;
  invoiceStatus?: string;
  currentInvoice?: number;
  futureInvoices?: number;
  debtTotal?: number;
  groups: DebtGroup[];
}

export const ClientHeader = ({
  photo,
  name,
  fields,
}: ClientHeaderProps): JSX.Element => (
  <St.BoxCard>
    <St.Summary>
      <St.Avatar>
        {photo ? <img src={photo} alt={name || 'Cliente'} /> : <FiUser />}
      </St.Avatar>
      <St.Fields>
        {fields.map((field) => (
          <St.Field key={field.label}>
            <St.Label>{field.label}</St.Label>
            <St.Value>{field.value}</St.Value>
          </St.Field>
        ))}
      </St.Fields>
    </St.Summary>
  </St.BoxCard>
);

export const ClientDebtColumn = ({
  dueDate,
  invoiceStatus,
  currentInvoice,
  futureInvoices,
  debtTotal,
  groups,
}: ClientDebtColumnProps): JSX.Element => (
  <St.SideColumn>
    <St.Panel>
      <St.PanelTitle>Resumo financeiro</St.PanelTitle>
      <St.FinanceSections>
        <St.FinanceSection>
          <St.SectionTitle>Última fatura (Atual)</St.SectionTitle>
          <St.PlainText>
            Vencimento
            {' '}
            {formatDate(dueDate)}
          </St.PlainText>
          <St.PlainText>Valor total</St.PlainText>
          <St.Amount>{formatMoney(currentInvoice)}</St.Amount>
          <St.Status>
            <St.StatusDot />
            {invoiceStatus || '-'}
          </St.Status>
        </St.FinanceSection>
        <St.FinanceSection>
          <St.SectionTitle>Faturas futuras</St.SectionTitle>
          <St.PlainText>Próximas faturas</St.PlainText>
          <St.PlainText>Valor total</St.PlainText>
          <St.Amount>{formatMoney(futureInvoices)}</St.Amount>
        </St.FinanceSection>
        <St.FinanceSection>
          <St.SectionTitle>Total da dívida</St.SectionTitle>
          <St.PlainText>Atual + futuras</St.PlainText>
          <St.PlainText>Valor total</St.PlainText>
          <St.Amount>{formatMoney(debtTotal)}</St.Amount>
          <St.SectionNote>
            {formatMoney(currentInvoice)}
            {' '}
            +
            {formatMoney(futureInvoices)}
          </St.SectionNote>
        </St.FinanceSection>
      </St.FinanceSections>
    </St.Panel>
    {groups.length === 0 ? (
      <St.Panel $compact>
        <St.MutedText>Não possui dívidas.</St.MutedText>
      </St.Panel>
    ) : (
      <St.DebtColumn>
        {groups.map((group) => (
          <St.DebtPanel key={group.label} $compact>
            <St.DebtType>{group.label}</St.DebtType>
            {group.items.map((item) => (
              <St.DebtCard key={item.id}>
                <St.DebtFields>
                  <St.FinanceSection>
                    <St.MutedText>Número do financiamento</St.MutedText>
                    <St.PlainText>{item.codigo || '-'}</St.PlainText>
                  </St.FinanceSection>
                  <St.FinanceSection>
                    <St.MutedText>Saldo devedor</St.MutedText>
                    <St.PlainText>{formatMoney(item.valor)}</St.PlainText>
                  </St.FinanceSection>
                  <St.FinanceSection>
                    <St.MutedText>Saldo devedor após antecipação</St.MutedText>
                    <St.PlainText>
                      {formatMoney(item.valorComDesconto)}
                    </St.PlainText>
                  </St.FinanceSection>
                  <St.FinanceSection>
                    <St.MutedText>Parcelas</St.MutedText>
                    <St.PlainText>
                      {item.parcelaAtual ?? '-'}
                      /
                      {item.totalParcelas ?? '-'}
                    </St.PlainText>
                  </St.FinanceSection>
                  <St.FinanceSection>
                    <St.MutedText>Vencimento</St.MutedText>
                    <St.PlainText>
                      {formatDate(item.dataVencimento || item.vencimento)}
                    </St.PlainText>
                  </St.FinanceSection>
                </St.DebtFields>
              </St.DebtCard>
            ))}
            <St.DebtNoticeRow>
              <St.InfoNotice>
                <St.InfoBadge>i</St.InfoBadge>
                <span>{group.notice}</span>
              </St.InfoNotice>
              <St.FooterButton>
                <Button outlined fitContent>
                  <span>Ver detalhes</span>
                </Button>
              </St.FooterButton>
            </St.DebtNoticeRow>
          </St.DebtPanel>
        ))}
      </St.DebtColumn>
    )}
  </St.SideColumn>
);
